import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
  Grid,
  Stack,
  InputAdornment,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Drawer,
  Card,
  CardContent,
  Skeleton,
  TableSortLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import api, { ENABLE_MOCK_DATA } from '../services/api';
import { formatDate } from '../utils/dateUtils';

// 自定义表格样式
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.common.white,
  borderBottom: '1px solid rgba(0, 255, 159, 0.1)',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 30, 60, 0.5)',
  },
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(0, 20, 40, 0.5)',
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 255, 159, 0.1) !important',
  },
  // 隐藏最后一行的边框
  '&:last-child td, &:last-child th': {
    borderBottom: 0,
  },
}));

const TYPE_LABELS = {
  standard: '标准',
  test: '测试',
  gift: '赠卡',
  promotion: '推广',
  other: '其他'
};

const AMOUNT_FILTERS = [
  { value: 'all', label: '全部额度' },
  { value: 'under100k', label: '10万以下' },
  { value: 'between100kAnd500k', label: '10万-50万' },
  { value: 'between500kAnd1000k', label: '50万-100万' },
  { value: 'between1000kAnd5000k', label: '100万-500万' },
  { value: 'above5000k', label: '500万以上' }
];

const EXPIRY_FILTERS = [
  { value: 'all', label: '全部有效期' },
  { value: 'expiring7', label: '7天内到期' },
  { value: 'expiring30', label: '30天内到期' },
  { value: 'expired', label: '已过期' },
  { value: 'validLong', label: '有效期>7天' }
];

const STATUS_META = {
  unused: {
    label: '未使用',
    background: 'rgba(0, 255, 0, 0.12)',
    color: '#00ff84',
    border: '1px solid rgba(0, 255, 0, 0.25)'
  },
  used: {
    label: '已使用',
    background: 'rgba(0, 160, 255, 0.12)',
    color: '#4da3ff',
    border: '1px solid rgba(0, 160, 255, 0.25)'
  },
  expired: {
    label: '已过期',
    background: 'rgba(255, 77, 79, 0.12)',
    color: '#ff8a80',
    border: '1px solid rgba(255, 77, 79, 0.2)'
  }
};

const numberFormatter = new Intl.NumberFormat('zh-CN');

const formatRemainingDays = (date) => {
  if (!date) {
    return '—';
  }
  const target = new Date(date);
  if (Number.isNaN(target.getTime())) {
    return '—';
  }

  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `已过期 ${Math.abs(diffDays)} 天`;
  }
  if (diffDays === 0) {
    return '今日到期';
  }
  return `剩余 ${diffDays} 天`;
};

const getStatusText = (status) => {
  switch (status) {
    case 'unused':
      return '未使用';
    case 'used':
      return '已使用';
    case 'expired':
      return '已过期';
    default:
      return status || '—';
  }
};

const CardKeyManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [cardKeys, setCardKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentCardKey, setCurrentCardKey] = useState(null);
  const [generateParams, setGenerateParams] = useState({
    count: 1,
    amount: 10000,
    isTestCard: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [generatedKeys, setGeneratedKeys] = useState([]);
  const [showGeneratedKeys, setShowGeneratedKeys] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [amountFilter, setAmountFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [detailsCardKey, setDetailsCardKey] = useState(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const systemStats = await api.getSystemStats('30d');
      const cardSummary = systemStats?.cardKeys || {};

      const [expiringSoonRes, expiredRes] = await Promise.allSettled([
        api.getAllCardKeys({ page: 1, limit: 1, expiryFilter: 'expiring7' }),
        api.getAllCardKeys({ page: 1, limit: 1, expiryFilter: 'expired' })
      ]);

      const extractTotal = (result) => {
        if (!result) return 0;
        if (typeof result.pagination?.total === 'number') {
          return result.pagination.total;
        }
        if (Array.isArray(result.cardKeys)) {
          return result.cardKeys.length;
        }
        return 0;
      };

      const expiringSoon =
        expiringSoonRes.status === 'fulfilled' ? extractTotal(expiringSoonRes.value) : 0;
      const expired =
        expiredRes.status === 'fulfilled' ? extractTotal(expiredRes.value) : 0;

      const total = cardSummary.total ?? cardSummary.count ?? 0;
      const used = cardSummary.used ?? 0;
      const unused = cardSummary.unused ?? 0;
      const usedInPeriod = cardSummary.usedInPeriod ?? 0;
      const usageRate = total > 0 ? Math.round(((used / total) * 1000)) / 10 : 0;

      setStats({
        total,
        used,
        unused,
        usedInPeriod,
        expiringSoon,
        expired,
        usageRate
      });
    } catch (error) {
      console.error('加载卡密统计失败:', error);
      if (!ENABLE_MOCK_DATA) {
        setSnackbar({
          open: true,
          message: '获取卡密统计失败，请稍后再试',
          severity: 'error'
        });
      }
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // 格式化金额显示
  const formatAmount = (amount) => {
    if (amount >= 10000000) return `${amount / 10000000}千万`;
    if (amount >= 1000000) return `${amount / 10000}万`;
    if (amount >= 10000) return `${amount / 10000}万`;
    return amount.toString();
  };

  // 加载卡密数据
  useEffect(() => {
    fetchCardKeys();
  }, [
    page,
    rowsPerPage,
    statusFilter,
    typeFilter,
    amountFilter,
    expiryFilter,
    sortBy,
    sortOrder,
    searchTerm
  ]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const fetchCardKeys = async () => {
    setLoading(true);
    try {
      const response = await api.getAllCardKeys({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm && searchTerm.trim() ? searchTerm.trim() : undefined,
        type: typeFilter,
        amountRange: amountFilter,
        expiryFilter,
        sort: sortBy,
        order: sortOrder
      });
      console.log('API返回的卡密数据:', response);
      setCardKeys(response.cardKeys || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (error) {
      console.error('获取卡密列表失败:', error);
      
      if (!ENABLE_MOCK_DATA) {
        setSnackbar({
          open: true,
          message: '获取卡密列表失败，请确保后端服务已启动',
          severity: 'error'
        });
        setCardKeys([]);
      } else {
        // 使用模拟数据
        console.warn('使用模拟数据');
        const mockCardKeys = [
          {
            id: 1,
            key: 'KICS-8HWF-BA29-KGES',
            amount: 20000,
            status: 'unused',
            type: 'standard',
            batchName: '模拟批次-A',
            createdAt: '2025-11-10T08:00:00.000Z',
            expiresAt: '2026-11-10T08:00:00.000Z',
            remarks: '示例卡密'
          },
          {
            id: 2,
            key: 'KICS-RRU2-FNHD-4RLY',
            amount: 20000,
            status: 'used',
            type: 'standard',
            batchName: '模拟批次-A',
            createdAt: '2025-11-09T06:00:00.000Z',
            expiresAt: '2026-11-09T06:00:00.000Z',
            usedAt: '2025-11-11T03:00:00.000Z',
            usedByName: 'test_user',
            remarks: '已使用示例'
          },
          {
            id: 3,
            key: 'KMCS-TEST-1ABC-DEF2',
            amount: 20000,
            status: 'unused',
            type: 'test',
            batchName: '测试批次',
            createdAt: '2025-11-08T02:30:00.000Z',
            expiresAt: '2026-11-08T02:30:00.000Z',
            remarks: '测试卡示例'
          },
          {
            id: 4,
            key: 'KM10W-9XYZ-1ABC-567Q',
            amount: 100000,
            status: 'unused',
            type: 'promotion',
            batchName: '推广批次',
            createdAt: '2025-10-01T05:15:00.000Z',
            expiresAt: '2026-01-01T05:15:00.000Z',
            remarks: '推广活动'
          }
        ];

        const now = new Date();
        const searchValue = searchTerm.trim().toLowerCase();

        let filteredMockKeys = mockCardKeys.filter((item) => {
          if (statusFilter !== 'all' && item.status !== statusFilter) {
            return false;
          }

          if (typeFilter !== 'all' && item.type !== typeFilter) {
            return false;
          }

          if (amountFilter !== 'all') {
            const amount = item.amount;
            switch (amountFilter) {
              case 'under100k':
                if (!(amount < 100000)) return false;
                break;
              case 'between100kAnd500k':
                if (!(amount >= 100000 && amount < 500000)) return false;
                break;
              case 'between500kAnd1000k':
                if (!(amount >= 500000 && amount < 1000000)) return false;
                break;
              case 'between1000kAnd5000k':
                if (!(amount >= 1000000 && amount < 5000000)) return false;
                break;
              case 'above5000k':
                if (!(amount >= 5000000)) return false;
                break;
              default:
                break;
            }
          }

          if (expiryFilter !== 'all') {
            const expiresAtDate = item.expiresAt ? new Date(item.expiresAt) : null;
            if (!expiresAtDate || Number.isNaN(expiresAtDate.getTime())) {
              return false;
            }
            const diffDays = Math.ceil((expiresAtDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            if (expiryFilter === 'expired' && diffDays >= 0) {
              return false;
            }
            if (expiryFilter.startsWith('expiring') && diffDays >= 0) {
              const days = parseInt(expiryFilter.replace('expiring', ''), 10) || 7;
              if (diffDays < 0 || diffDays > days) {
                return false;
              }
            }
            if (expiryFilter === 'validLong' && diffDays <= 7) {
              return false;
            }
          }

          if (searchValue) {
            const haystack = `${item.key} ${item.batchName || ''} ${item.remarks || ''}`.toLowerCase();
            if (!haystack.includes(searchValue)) {
              return false;
            }
          }

          return true;
        });

        setCardKeys(filteredMockKeys);
        setTotalCount(filteredMockKeys.length);
        setSnackbar({
          open: true,
          message: '使用模拟数据（无法连接到API）',
          severity: 'warning'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    setPage(0);
    setSortOrder((prevOrder) =>
      sortBy === property ? (prevOrder === 'asc' ? 'desc' : 'asc') : 'desc'
    );
    setSortBy(property);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0);
  };

  const handleAmountFilterChange = (event) => {
    setAmountFilter(event.target.value);
    setPage(0);
  };

  const handleExpiryFilterChange = (event) => {
    setExpiryFilter(event.target.value);
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setAmountFilter('all');
    setExpiryFilter('all');
    setSearchTerm('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setPage(0);
  };

  const escapeCsv = (value) => {
    if (value === null || value === undefined) {
      return '""';
    }
    const str = String(value).replace(/"/g, '""');
    return `"${str}"`;
  };

  const handleExportCurrentPage = () => {
    if (!Array.isArray(cardKeys) || cardKeys.length === 0) {
      setSnackbar({
        open: true,
        message: '当前没有可导出的卡密数据',
        severity: 'info'
      });
      return;
    }

    const headers = [
      '卡密',
      '面额',
      '状态',
      '类型',
      '批次',
      '创建时间',
      '过期时间',
      '使用者',
      '使用时间',
      '备注'
    ];

    const rows = cardKeys.map((key) => {
      const normalizedStatus =
        key.status === 'unused' &&
        key.expiresAt &&
        new Date(key.expiresAt) < new Date()
          ? 'expired'
          : key.status;

      return [
        key.key,
        formatAmount(key.amount),
        STATUS_META[normalizedStatus]?.label || getStatusText(normalizedStatus),
        TYPE_LABELS[key.type] || key.type || '—',
        key.batchName || '—',
        formatDate(key.createdAt),
        formatDate(key.expiresAt),
        key.usedByName || key.usedBy || '—',
        key.usedAt ? formatDate(key.usedAt) : '—',
        key.remarks || ''
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `卡密列表_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenDetails = (cardKey) => {
    setDetailsCardKey(cardKey);
  };

  const handleCloseDetails = () => {
    setDetailsCardKey(null);
  };

  const handleManualRefresh = () => {
    fetchCardKeys();
    loadStats();
  };

  const summaryCards = useMemo(() => {
    return [
      {
        key: 'total',
        title: '卡密总数',
        value: stats?.total ?? 0,
        caption: `使用率 ${stats?.usageRate ?? 0}%`,
        color: 'rgba(77, 208, 225, 0.35)'
      },
      {
        key: 'unused',
        title: '未使用',
        value: stats?.unused ?? 0,
        caption: '待分发或售卖',
        color: 'rgba(118, 255, 3, 0.28)'
      },
      {
        key: 'used',
        title: '已使用',
        value: stats?.used ?? 0,
        caption: `近30天使用 ${stats?.usedInPeriod ?? 0}`,
        color: 'rgba(129, 212, 250, 0.3)'
      },
      {
        key: 'expiringSoon',
        title: '即将到期',
        value: stats?.expiringSoon ?? 0,
        caption: `已过期 ${stats?.expired ?? 0}`,
        color: 'rgba(255, 171, 64, 0.28)'
      }
    ];
  }, [stats]);

  const filterControlSx = {
    minWidth: { xs: '100%', sm: 160 },
    backgroundColor: 'rgba(0, 20, 40, 0.5)',
    borderRadius: '8px',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(0, 255, 159, 0.2)'
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 255, 159, 0.5)'
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main'
      }
    }
  };

  const searchFieldSx = {
    minWidth: { xs: '100%', sm: 220 },
    backgroundColor: 'rgba(0, 20, 40, 0.5)',
    borderRadius: '8px',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(0, 255, 159, 0.2)'
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 255, 159, 0.5)'
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main'
      }
    }
  };

  const detailItems = useMemo(() => {
    if (!detailsCardKey) {
      return [];
    }

    const normalizedStatus =
      detailsCardKey.status === 'unused' &&
      detailsCardKey.expiresAt &&
      new Date(detailsCardKey.expiresAt) < new Date()
        ? 'expired'
        : detailsCardKey.status;

    return [
      { label: '卡密', value: detailsCardKey.key },
      { label: '面额', value: formatAmount(detailsCardKey.amount) },
      {
        label: '状态',
        value: STATUS_META[normalizedStatus]?.label || getStatusText(detailsCardKey.status)
      },
      { label: '类型', value: TYPE_LABELS[detailsCardKey.type] || detailsCardKey.type || '—' },
      { label: '批次', value: detailsCardKey.batchName || '—' },
      { label: '创建时间', value: formatDate(detailsCardKey.createdAt) },
      {
        label: '过期时间',
        value: formatDate(detailsCardKey.expiresAt),
        description: formatRemainingDays(detailsCardKey.expiresAt)
      },
      {
        label: '使用者',
        value: detailsCardKey.usedByName || detailsCardKey.usedBy || '—'
      },
      {
        label: '使用时间',
        value: detailsCardKey.usedAt ? formatDate(detailsCardKey.usedAt) : '—'
      },
      { label: '备注', value: detailsCardKey.remarks || '—' }
    ];
  }, [detailsCardKey]);

  // 直接使用从后端返回的卡密数据，后端已经处理了搜索和分页
  const currentPageKeys = useMemo(() => {
    return Array.isArray(cardKeys) ? cardKeys : [];
  }, [cardKeys]);

  // 修改翻页处理，确保搜索和状态筛选条件保持
  const handleChangePage = (event, newPage) => {
    console.log('切换到页码:', newPage);
    setPage(newPage);
    // 重新加载数据由useEffect监听page变化触发
  };

  // 处理搜索输入变化（添加防抖，当用户输入时重置页码）
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPage(0); // 重置到第一页
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 打开生成卡密对话框
  const handleOpenGenerateDialog = () => {
    setOpenGenerateDialog(true);
  };

  // 处理生成卡密参数变更
  const handleChangeGenerateParams = (e) => {
    const { name, value } = e.target;
    
    // 调试日志
    console.log(`更新生成参数: ${name} = ${value} (${typeof value})`);
    
    // 对于count字段，进行特殊处理
    if (name === 'count') {
      // 允许空值（用于删除）
      if (value === '' || value === null || value === undefined) {
    setGenerateParams(prev => ({
      ...prev,
          [name]: ''
        }));
        return;
      }
      
      // 只允许数字
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return; // 不是数字，不更新
      }
      
      // 限制最大值不能超过10000
      if (numValue > 10000) {
        return; // 超过最大值，不更新
      }
      
      // 允许0和负数（用于删除），但提交时会验证
      setGenerateParams(prev => ({
        ...prev,
        [name]: numValue
      }));
      return;
    }
    
    // 确保amount是数字
    setGenerateParams(prev => ({
      ...prev,
      [name]: name === 'amount' ? Number(value) : value
    }));
  };

  // 生成卡密
  const handleGenerateCardKeys = async () => {
    try {
      setGenerating(true);
      // 调用API生成卡密 - 修复函数参数和调用
      try {
        // 确保金额是数字类型
        const amount = Number(generateParams.amount);
        
        // 验证数量
        if (generateParams.count === '' || generateParams.count === null || generateParams.count === undefined) {
          setSnackbar({
            open: true,
            message: '请输入生成数量',
            severity: 'error'
          });
          setGenerating(false);
          return;
        }
        
        const count = Number(generateParams.count);
        
        // 验证数量范围
        if (isNaN(count) || count <= 0 || count > 10000) {
          setSnackbar({
            open: true,
            message: '生成数量必须在1-10000之间',
            severity: 'error'
          });
          setGenerating(false);
          return;
        }
        
        console.log(`准备生成卡密 - 数量: ${count}, 金额: ${amount} (${typeof amount}), 测试卡: ${generateParams.isTestCard}`);
        
        // 使用正确的API函数和参数
        const response = await api.generateCardKeys(
          count, 
          amount, 
          generateParams.isTestCard ? 'test' : 'standard',  // 类型
          generateParams.isTestCard ? '测试卡（3万字，KMCS前缀）' : '' // 备注
        );
        console.log('生成卡密响应:', response);
        
        // 处理响应
        const cardKeysData = response.cardKeys || (Array.isArray(response) ? response : []);
        setGeneratedKeys(cardKeysData);
        
        // 确保cardKeys是数组并刷新列表
        setCardKeys(Array.isArray(cardKeys) 
          ? [...cardKeysData, ...cardKeys] 
          : cardKeysData);
        setShowGeneratedKeys(true);
        
        // 成功后刷新列表
        fetchCardKeys();
        loadStats();
        
        setSnackbar({
          open: true,
          message: response.message || `成功生成${cardKeysData.length}个卡密`,
          severity: 'success'
        });
      } catch (error) {
        console.error('生成卡密失败:', error);
        
        if (!ENABLE_MOCK_DATA) {
          setSnackbar({
            open: true,
            message: '生成卡密失败，请确保后端服务已启动',
            severity: 'error'
          });
        } else {
          // 使用模拟数据
          console.warn('使用模拟数据');
          
          // 生成随机字符串的函数
          const getRandomString = (length) => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            let result = '';
            for (let i = 0; i < length; i++) {
              result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
          };
          
          // 计算过期日期（默认365天）
          const getExpiryDate = (days = 365) => {
            const date = new Date();
            date.setDate(date.getDate() + days);
            return date.toISOString().split('T')[0];
          };
          
          const mockGeneratedKeys = Array.from({ length: generateParams.count }, (_, i) => {
            // 如果是测试卡，使用KMCS前缀
            const prefix = generateParams.isTestCard ? 'KMCS' : getRandomString(4);
            const randomKey = generateParams.isTestCard 
              ? `KMCS-${getRandomString(4)}-${getRandomString(4)}-${getRandomString(4)}`
              : `${getRandomString(4)}-${getRandomString(4)}-${getRandomString(4)}-${getRandomString(4)}`;
            return {
              id: (Array.isArray(cardKeys) ? cardKeys.length : 0) + i + 1,
              key: randomKey,
              amount: generateParams.isTestCard ? 20000 : generateParams.amount,
              status: 'unused',
              createdAt: new Date().toISOString().split('T')[0],
              expiresAt: getExpiryDate(365)
            };
          });
          
          setGeneratedKeys(mockGeneratedKeys);
          setShowGeneratedKeys(true);
          // 确保cardKeys是数组
          setCardKeys(Array.isArray(cardKeys) 
            ? [...mockGeneratedKeys, ...cardKeys] 
            : mockGeneratedKeys);
          
          setSnackbar({
            open: true,
            message: `成功生成 ${generateParams.count} 个卡密（模拟）`,
            severity: 'success'
          });
        }
      }
    } catch (error) {
      console.error('生成卡密失败:', error);
      setSnackbar({
        open: true,
        message: '生成卡密失败',
        severity: 'error'
      });
    } finally {
      setGenerating(false);
      setOpenGenerateDialog(false);
    }
  };

  // 打开删除确认对话框
  const handleDeleteCardKey = (cardKey) => {
    setCurrentCardKey(cardKey);
    setOpenDeleteDialog(true);
  };

  // 确认删除卡密
  const handleConfirmDelete = async () => {
    try {
      const cardKeyId = currentCardKey._id || currentCardKey.id;
      console.log('删除卡密，ID:', cardKeyId);
      
      await api.deleteCardKey(cardKeyId);
      
      // 从列表中移除已删除的卡密
      if (Array.isArray(cardKeys)) {
        setCardKeys(cardKeys.filter(cardKey => {
          const id = cardKey._id || cardKey.id;
          return id !== cardKeyId;
        }));
      }
      
      // 更新总数
      setTotalCount(prev => prev - 1);
      
      setSnackbar({
        open: true,
        message: '卡密删除成功',
        severity: 'success'
      });
      setOpenDeleteDialog(false);
      
      // 重新加载卡密列表以确保数据一致
      fetchCardKeys();
      loadStats();
    } catch (error) {
      console.error('删除卡密失败:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || '删除卡密失败',
        severity: 'error'
      });
    }
  };

  // 复制卡密到剪贴板
  const handleCopyCardKey = (key) => {
    // 创建临时textarea
    const textarea = document.createElement('textarea');
    textarea.value = key;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      // 执行复制
      document.execCommand('copy');
        setSnackbar({
          open: true,
          message: '卡密已复制到剪贴板',
          severity: 'success'
        });
    } catch (err) {
      console.error('复制失败:', err);
        setSnackbar({
          open: true,
          message: '复制失败',
          severity: 'error'
        });
    } finally {
      // 清理临时元素
      document.body.removeChild(textarea);
      }
  };

  // 将生成的卡密导出为CSV
  const handleExportCSV = () => {
    if (!Array.isArray(generatedKeys) || generatedKeys.length === 0) {
      return;
    }
    
    const headers = ['卡密', '金额', '状态', '创建日期', '过期日期'];
    const csvContent = [
      headers.join(','),
      ...generatedKeys.map(key => [
        key.key,
        formatAmount(key.amount),
        getStatusText(key.status),
        key.createdAt,
        key.expiresAt
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `卡密导出_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 关闭snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 辅助函数：生成随机字符串
  const getRandomString = (length) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  // 辅助函数：计算过期日期（默认365天）
  const getExpiryDate = (days = 365) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  // 处理状态筛选变化
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // 重置到第一页
  };

  return (
    <Box p={3}>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.key}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  background: 'rgba(0, 40, 70, 0.6)',
                  border: `1px solid ${card.color}`,
                  color: '#fff',
                  minHeight: 112
                }}
              >
                {statsLoading ? (
                  <Skeleton
                    variant="rectangular"
                    height={112}
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)', borderRadius: '16px' }}
                  />
                ) : (
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)' }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {numberFormatter.format(card.value)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      {card.caption}
                    </Typography>
                  </CardContent>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={3}
          sx={{
            padding: 3,
            background: 'rgba(0, 30, 60, 0.72)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 255, 159, 0.2)',
            borderRadius: '16px'
          }}
        >
          <Stack spacing={3}>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              gap={2}
            >
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                卡密管理
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Tooltip title="刷新列表">
                  <IconButton
                    size="small"
                    onClick={handleManualRefresh}
                    sx={{
                      color: 'rgba(0, 255, 159, 0.85)',
                      border: '1px solid rgba(0, 255, 159, 0.3)',
                      backgroundColor: 'rgba(0, 255, 159, 0.08)'
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportCurrentPage}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none'
                  }}
                >
                  导出当前页
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenGenerateDialog}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  生成卡密
                </Button>
              </Stack>
            </Box>

            <Stack
              direction={{ xs: 'column', xl: 'row' }}
              spacing={1.5}
              flexWrap="wrap"
              alignItems={{ xl: 'center' }}
            >
              <FormControl variant="outlined" size="small" sx={filterControlSx}>
                <InputLabel id="status-filter-label">状态</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="状态"
                >
                  <MenuItem value="all">全部</MenuItem>
                  <MenuItem value="unused">未使用</MenuItem>
                  <MenuItem value="used">已使用</MenuItem>
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" sx={filterControlSx}>
                <InputLabel id="type-filter-label">类型</InputLabel>
                <Select
                  labelId="type-filter-label"
                  value={typeFilter}
                  onChange={handleTypeFilterChange}
                  label="类型"
                >
                  <MenuItem value="all">全部类型</MenuItem>
                  {Object.entries(TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" sx={filterControlSx}>
                <InputLabel id="amount-filter-label">额度</InputLabel>
                <Select
                  labelId="amount-filter-label"
                  value={amountFilter}
                  onChange={handleAmountFilterChange}
                  label="额度"
                >
                  {AMOUNT_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" sx={filterControlSx}>
                <InputLabel id="expiry-filter-label">有效期</InputLabel>
                <Select
                  labelId="expiry-filter-label"
                  value={expiryFilter}
                  onChange={handleExpiryFilterChange}
                  label="有效期"
                >
                  {EXPIRY_FILTERS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                placeholder="搜索卡号或备注..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={searchFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                    </InputAdornment>
                  )
                }}
              />

              <Button
                variant="text"
                color="inherit"
                startIcon={<FilterListIcon />}
                onClick={handleResetFilters}
                sx={{
                  color: 'rgba(255,255,255,0.75)',
                  textTransform: 'none'
                }}
              >
                重置筛选
              </Button>
            </Stack>

            {showGeneratedKeys && Array.isArray(generatedKeys) && generatedKeys.length > 0 && (
              <Paper
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(0, 40, 70, 0.7)',
                  border: '1px solid rgba(0, 255, 159, 0.3)'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    最近生成的卡密
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportCSV}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none'
                    }}
                  >
                    导出CSV
                  </Button>
                </Box>
                <Divider sx={{ borderColor: 'rgba(0, 255, 159, 0.1)', my: 1 }} />
                <Box sx={{ maxHeight: 150, overflowY: 'auto', pr: 1 }}>
                  <Grid container spacing={1}>
                    {generatedKeys.map((key, index) => (
                      <Grid item xs={12} md={6} key={key.id || key.key || index}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            borderRadius: '8px',
                            backgroundColor: 'rgba(0, 20, 40, 0.5)',
                            border: '1px solid rgba(0, 255, 159, 0.1)'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {key.key}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyCardKey(key.key)}
                            sx={{ color: 'primary.main' }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress color="primary" />
              </Box>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    borderRadius: '12px',
                    overflowX: 'auto',
                    border: '1px solid rgba(0, 255, 159, 0.1)'
                  }}
                >
                  <Table size="small" sx={{ tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(0, 40, 70, 0.8)' }}>
                        <StyledTableCell sx={{ minWidth: 200, maxWidth: 400 }}>卡密</StyledTableCell>
                        <StyledTableCell sortDirection={sortBy === 'amount' ? sortOrder : false}>
                          <TableSortLabel
                            active={sortBy === 'amount'}
                            direction={sortBy === 'amount' ? sortOrder : 'asc'}
                            onClick={() => handleRequestSort('amount')}
                          >
                            面额
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell>状态</StyledTableCell>
                        <StyledTableCell>类型</StyledTableCell>
                        <StyledTableCell>批次</StyledTableCell>
                        <StyledTableCell sortDirection={sortBy === 'createdAt' ? sortOrder : false}>
                          <TableSortLabel
                            active={sortBy === 'createdAt'}
                            direction={sortBy === 'createdAt' ? sortOrder : 'desc'}
                            onClick={() => handleRequestSort('createdAt')}
                          >
                            创建时间
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell sortDirection={sortBy === 'expiresAt' ? sortOrder : false}>
                          <TableSortLabel
                            active={sortBy === 'expiresAt'}
                            direction={sortBy === 'expiresAt' ? sortOrder : 'desc'}
                            onClick={() => handleRequestSort('expiresAt')}
                          >
                            过期时间
                          </TableSortLabel>
                        </StyledTableCell>
                        <StyledTableCell>使用者</StyledTableCell>
                        <StyledTableCell>使用时间</StyledTableCell>
                        <StyledTableCell align="center">操作</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentPageKeys.length > 0 ? (
                        currentPageKeys.map((cardKey, idx) => {
                          const rowKey = cardKey._id || cardKey.id || cardKey.key || idx;
                          const isExpired =
                            cardKey.status === 'unused' &&
                            cardKey.expiresAt &&
                            new Date(cardKey.expiresAt) < new Date();
                          const normalizedStatus = isExpired ? 'expired' : cardKey.status;
                          const statusMeta = STATUS_META[normalizedStatus] || STATUS_META.unused;
                          const typeLabel = TYPE_LABELS[cardKey.type] || cardKey.type || '—';
                          const createdLabel = formatDate(cardKey.createdAt);
                          const expiresLabel = formatDate(cardKey.expiresAt);
                          const remainingLabel = formatRemainingDays(cardKey.expiresAt);
                          const usedLabel = cardKey.usedAt ? formatDate(cardKey.usedAt) : '—';
                          const usedByLabel = cardKey.usedByName || cardKey.usedBy || '—';
                          return (
                            <StyledTableRow key={rowKey}>
                              <StyledTableCell sx={{ minWidth: 200, maxWidth: 400 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                    <Typography
                                      variant="body2"
                                      component="div"
                                      sx={{
                                        fontFamily: 'JetBrains Mono, Menlo, monospace',
                                        fontWeight: 600,
                                        wordBreak: 'break-word',
                                        overflowWrap: 'anywhere',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.5
                                      }}
                                    >
                                      {cardKey.key}
                                    </Typography>
                                    {cardKey.remarks && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mt: 0.5 }}
                                      >
                                        {cardKey.remarks}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Tooltip title="复制卡密">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCopyCardKey(cardKey.key)}
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="查看详情">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDetails(cardKey)}
                                      sx={{ color: 'rgba(129, 212, 250, 0.9)' }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 96 }}>
                                <Typography variant="body2" align="right">
                                  {formatAmount(cardKey.amount)}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 96 }}>
                                <Chip
                                  label={statusMeta.label}
                                  size="small"
                                  sx={{
                                    backgroundColor: statusMeta.background,
                                    color: statusMeta.color,
                                    border: statusMeta.border,
                                    fontWeight: 600
                                  }}
                                />
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 96 }}>
                                <Chip
                                  label={typeLabel}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      cardKey.type === 'test'
                                        ? 'rgba(129, 212, 250, 0.18)'
                                        : 'rgba(0, 255, 159, 0.18)',
                                    color:
                                      cardKey.type === 'test'
                                        ? '#81d4fa'
                                        : 'rgba(0, 255, 159, 0.9)',
                                    border:
                                      cardKey.type === 'test'
                                        ? '1px solid rgba(129, 212, 250, 0.4)'
                                        : '1px solid rgba(0, 255, 159, 0.4)'
                                  }}
                                />
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 120 }}>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                  {cardKey.batchName || '—'}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 150 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                                  {createdLabel}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 150 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                                  {expiresLabel}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                                  {remainingLabel}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 120 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                                  {usedByLabel}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: 150 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                                  {usedLabel}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell align="center" sx={{ width: 110 }}>
                                <Tooltip title="删除">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteCardKey(cardKey)}
                                      sx={{
                                        color: '#ff4d4f',
                                        '&:hover': { backgroundColor: 'rgba(255, 77, 79, 0.12)' }
                                      }}
                                      disabled={cardKey.status === 'used'}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })
                      ) : (
                        <StyledTableRow>
                          <StyledTableCell colSpan={11} align="center">
                            暂无数据
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={totalCount || currentPageKeys.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="每页行数:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} / 共 ${count}`}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '& .MuiTablePagination-select': {
                      color: 'white'
                    },
                    '& .MuiTablePagination-selectIcon': {
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                />
              </>
            )}
          </Stack>
        </Paper>
      </Stack>

      {/* 生成卡密对话框 */}
      <Dialog 
        open={openGenerateDialog} 
        onClose={() => setOpenGenerateDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 255, 159, 0.2)',
            borderRadius: '16px',
            minWidth: { xs: '95vw', sm: 500 },
            maxWidth: { xs: '95vw', sm: 500 },
            m: { xs: 1, sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(0, 255, 159, 0.1)', color: 'primary.main' }}>
          生成卡密
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                margin="normal"
                label="生成数量"
                  name="count"
                type="number"
                value={generateParams.count === '' ? '' : generateParams.count}
                  onChange={handleChangeGenerateParams}
                inputProps={{ min: 1, max: 10000 }}
                helperText="请输入1-10000之间的数量"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 255, 159, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 255, 159, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="card-key-type-label">卡密类型</InputLabel>
                <Select
                  labelId="card-key-type-label"
                  id="card-key-type"
                  name="isTestCard"
                  value={generateParams.isTestCard ? 'test' : 'standard'}
                  onChange={(e) => {
                    const isTest = e.target.value === 'test';
                    setGenerateParams(prev => ({
                      ...prev,
                      isTestCard: isTest,
                      amount: isTest ? 20000 : (prev.amount || 10000)
                    }));
                  }}
                  label="卡密类型"
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 255, 159, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 255, 159, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <MenuItem value="standard">普通卡密</MenuItem>
                  <MenuItem value="test">测试卡（3万字，KMCS前缀）</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!generateParams.isTestCard && (
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="card-key-amount-label">卡密金额</InputLabel>
                <Select
                  labelId="card-key-amount-label"
                  id="card-key-amount"
                  name="amount"
                  value={generateParams.amount}
                  onChange={handleChangeGenerateParams}
                  label="卡密金额"
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 255, 159, 0.2)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 255, 159, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {[10000, 50000, 100000, 300000, 500000, 1000000, 5000000, 10000000].map((value) => (
                    <MenuItem key={value} value={value}>
                      {value === 10000 ? '1万' : 
                       value === 50000 ? '5万' : 
                       value === 100000 ? '10万' : 
                       value === 300000 ? '30万' : 
                       value === 500000 ? '50万' : 
                       value === 1000000 ? '100万' : 
                       value === 5000000 ? '500万' : 
                       value === 10000000 ? '1000万' : value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}
            {generateParams.isTestCard && (
            <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'primary.main', mt: 1, mb: 1 }}>
                  测试卡固定金额：3万字（30000字），有效期：365天
                </Typography>
            </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(0, 255, 159, 0.1)' }}>
          <Button 
            onClick={() => setOpenGenerateDialog(false)} 
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            取消
          </Button>
          <Button 
            onClick={handleGenerateCardKeys} 
            variant="contained" 
            color="primary"
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            生成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 77, 79, 0.2)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#ff4d4f' }}>确认删除</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            确定要删除卡密 <b>{currentCardKey?.key}</b> 吗？该操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 77, 79, 0.1)' }}>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            取消
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            sx={{ 
              backgroundColor: '#ff4d4f',
              '&:hover': { backgroundColor: '#ff7875' },
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={Boolean(detailsCardKey)}
        onClose={handleCloseDetails}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 380 },
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(12px)',
            borderLeft: '1px solid rgba(0, 255, 159, 0.24)',
            color: '#fff'
          }
        }}
      >
        <Box p={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            卡密详情
          </Typography>
          <Stack spacing={2} sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
            {detailItems.length ? (
              detailItems.map((item) => (
                <Box key={item.label}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {item.value}
                  </Typography>
                  {item.description && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                选择卡密查看详细信息
              </Typography>
            )}
          </Stack>
          {detailsCardKey && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ContentCopyIcon />}
              onClick={() => handleCopyCardKey(detailsCardKey.key)}
              sx={{ textTransform: 'none', borderRadius: '8px' }}
            >
              复制卡密
            </Button>
          )}
        </Box>
      </Drawer>

      {/* 提示信息 */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CardKeyManagement; 